import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useSourcingItems, useCompanySettings, useUpsertCompanySettings } from '@/hooks/use-store';
import { useToast } from '@/hooks/use-toast';
import { Save, RotateCcw } from 'lucide-react';
import type { PseoConfig } from '@/lib/store-types';

// ---------------------------------------------------------------------------
// Defaults (must mirror seo-generate.ts so the admin sees what the worker uses)
// ---------------------------------------------------------------------------

const DEFAULT_PROMPT = `ERES: Un experto en SEO B2B y comercio internacional (FMCG) para LATAM.
TAREA: Genera JSON SEO adaptando tono al Ángulo de Venta indicado en los datos.
REGLAS JSON:
1. meta_title: Máx 60 chars. Comercial. EN ESPAÑOL.
2. meta_description: Máx 155 chars. Incluye puertos asignados + Call to Action.
3. assigned_ports: Array con los 3 puertos indicados.
4. target_ports: Array con 15-25 puertos logísticamente relevantes.
5. search_intent: Siempre "b2b_wholesale_latam".
6. extra_keywords: Array de 3-5 palabras clave en español.
7. ai_faqs: Array de 2 objetos { "pregunta": "...", "respuesta": "..." } respondiendo las preguntas indicadas.
8. seo_slug: URL en español con sufijo B2B. Máx 60 caracteres.
RESPONDE SOLO JSON.`;

const DEFAULT_PORT_REGIONS: PseoConfig['master_ports'] = [
  {
    region: 'Caribe',
    ports: ['Caucedo', 'Haina', 'Mariel', 'La Habana', 'Santiago de Cuba', 'Kingston', 'San Juan', 'Freeport', 'Port of Spain', 'Port-au-Prince'],
  },
  {
    region: 'Centroamérica / Panamá',
    ports: ['Zona Libre de Colón', 'Manzanillo', 'Balboa', 'Cristóbal', 'Puerto Limón', 'Moín', 'Caldera', 'Puerto Cortés', 'Santo Tomás de Castilla', 'Puerto Barrios', 'Puerto Quetzal', 'Acajutla', 'Corinto'],
  },
  {
    region: 'Venezuela / Colombia',
    ports: ['Puerto Cabello', 'La Guaira', 'Maracaibo', 'Cartagena', 'Barranquilla', 'Santa Marta', 'Buenaventura'],
  },
  {
    region: 'Resto Sudamérica',
    ports: ['Guayaquil', 'Manta', 'Callao', 'Paita', 'San Antonio', 'Valparaíso', 'Iquique', 'Santos', 'Paranaguá', 'Suape', 'Montevideo', 'Buenos Aires'],
  },
  {
    region: 'Norteamérica / México',
    ports: ['Miami', 'Port Everglades', 'Houston', 'Veracruz', 'Altamira', 'Manzanillo (MX)', 'Lázaro Cárdenas'],
  },
];

/** Serialize port regions to editable text: "## Region\nPort1, Port2\n\n## Region2\n..." */
function portsToText(regions: PseoConfig['master_ports']): string {
  return (regions ?? DEFAULT_PORT_REGIONS)!
    .map(r => `## ${r.region}\n${r.ports.join(', ')}`)
    .join('\n\n');
}

/** Parse text back to port regions. Expects "## Region\nPort1, Port2" blocks. */
function textToPorts(text: string): PseoConfig['master_ports'] {
  const blocks = text.split(/^##\s+/m).filter(Boolean);
  return blocks.map(block => {
    const lines = block.trim().split('\n');
    const region = lines[0].trim();
    const ports = lines
      .slice(1)
      .join(',')
      .split(',')
      .map(p => p.trim())
      .filter(Boolean);
    return { region, ports };
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AdminPseoConfig = () => {
  const { data: settings, isLoading: loadingSettings } = useCompanySettings();
  const upsert = useUpsertCompanySettings();
  const { data: items = [], isLoading: loadingItems } = useSourcingItems(null, { admin: true });
  const { toast } = useToast();

  // Editable form state
  const [prompt, setPrompt] = useState('');
  const [portsText, setPortsText] = useState('');
  const [model, setModel] = useState('gemini-2.0-flash');
  const [batchSize, setBatchSize] = useState(5);
  const [delayMs, setDelayMs] = useState(2000);
  const [version, setVersion] = useState(1);
  const [dirty, setDirty] = useState(false);

  // Populate form from DB (or defaults) once settings load
  useEffect(() => {
    if (loadingSettings) return;
    const cfg = settings?.pseo_config;
    setPrompt(cfg?.system_prompt?.trim() || DEFAULT_PROMPT);
    setPortsText(portsToText(cfg?.master_ports ?? DEFAULT_PORT_REGIONS));
    setModel(cfg?.model?.trim() || 'gemini-2.0-flash');
    setBatchSize(cfg?.batch_size ?? 5);
    setDelayMs(cfg?.delay_ms ?? 2000);
    setVersion(cfg?.version ?? 1);
    setDirty(false);
  }, [settings, loadingSettings]);

  const markDirty = () => setDirty(true);

  const handleSave = async () => {
    const pseoConfig: PseoConfig = {
      system_prompt: prompt,
      master_ports: textToPorts(portsText),
      model,
      batch_size: batchSize,
      delay_ms: delayMs,
      version,
    };

    try {
      await upsert.mutateAsync({
        ...(settings?.id ? { id: settings.id } : { legal_name: settings?.legal_name ?? 'Padmora' }),
        pseo_config: pseoConfig,
      } as Parameters<typeof upsert.mutateAsync>[0]);

      setDirty(false);
      toast({ title: 'Guardado', description: 'Configuración pSEO actualizada.' });
    } catch (err) {
      toast({ title: 'Error al guardar', description: String(err), variant: 'destructive' });
    }
  };

  const handleReset = () => {
    setPrompt(DEFAULT_PROMPT);
    setPortsText(portsToText(DEFAULT_PORT_REGIONS));
    setModel('gemini-2.0-flash');
    setBatchSize(5);
    setDelayMs(2000);
    setVersion(v => v); // keep current version on reset (intentional — version is only bumped manually)
    setDirty(true);
  };

  // Stats — includes per-version breakdown so admin can see who's outdated
  const stats = useMemo(() => {
    const total = items.length;
    const withSeo = items.filter(i => i.seo_metadata?.meta_title && i.seo_metadata?.meta_description).length;
    const currentVersion = items.filter(i => (i.seo_metadata?.pseo_version ?? 0) === version).length;
    const outdated = withSeo - currentVersion;
    return { total, withSeo, withoutSeo: total - withSeo, currentVersion, outdated };
  }, [items, version]);

  const pct = stats.total > 0 ? Math.round((stats.withSeo / stats.total) * 100) : 0;

  if (loadingSettings) {
    return <p className="text-sm text-muted-foreground py-8">Cargando configuración…</p>;
  }

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Configuración pSEO</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} disabled={upsert.isPending}>
            <RotateCcw className="h-4 w-4 mr-1" />
            Restaurar defaults
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!dirty || upsert.isPending}>
            <Save className="h-4 w-4 mr-1" />
            {upsert.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* ── Section 1: AI Prompt ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Prompt de IA (System Prompt)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="pseo-prompt">
            Este prompt se envía a Gemini como instrucción del sistema al generar metadatos pSEO.
          </Label>
          <Textarea
            id="pseo-prompt"
            value={prompt}
            onChange={e => { setPrompt(e.target.value); markDirty(); }}
            rows={20}
            className="font-mono text-xs leading-relaxed"
          />
        </CardContent>
      </Card>

      {/* ── Section 2: Master Port List ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lista Maestra de Puertos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="pseo-ports">
            Cada región empieza con <code className="text-xs bg-muted px-1 rounded">## Región</code>, seguido de puertos separados por comas. Se eligen 3 al azar por producto.
          </Label>
          <Textarea
            id="pseo-ports"
            value={portsText}
            onChange={e => { setPortsText(e.target.value); markDirty(); }}
            rows={14}
            className="font-mono text-xs leading-relaxed"
          />
          <p className="text-xs text-muted-foreground">
            Total puertos: {textToPorts(portsText).reduce((n, r) => n + r.ports.length, 0)}
          </p>
        </CardContent>
      </Card>

      {/* ── Section 3: Model & Batch Settings ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configuración del modelo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="pseo-version">Versión pSEO</Label>
              <Input
                id="pseo-version"
                type="number"
                min={1}
                value={version}
                onChange={e => { setVersion(Math.max(1, Number(e.target.value) || 1)); markDirty(); }}
              />
              <p className="text-xs text-muted-foreground">Incrementar al cambiar prompt/puertos.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="pseo-model">Modelo Gemini</Label>
              <Input
                id="pseo-model"
                value={model}
                onChange={e => { setModel(e.target.value); markDirty(); }}
                placeholder="gemini-2.0-flash"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pseo-batch">Items por lote</Label>
              <Input
                id="pseo-batch"
                type="number"
                min={1}
                max={20}
                value={batchSize}
                onChange={e => { setBatchSize(Number(e.target.value) || 5); markDirty(); }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pseo-delay">Delay entre lotes (ms)</Label>
              <Input
                id="pseo-delay"
                type="number"
                min={500}
                max={10000}
                step={500}
                value={delayMs}
                onChange={e => { setDelayMs(Number(e.target.value) || 2000); markDirty(); }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Section 4: Stats ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Estadísticas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingItems ? (
            <p className="text-sm text-muted-foreground">Cargando productos…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total items</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.currentVersion}</p>
                  <p className="text-xs text-muted-foreground">pSEO v{version}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.outdated}</p>
                  <p className="text-xs text-muted-foreground">Versión antigua</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.withoutSeo}</p>
                  <p className="text-xs text-muted-foreground">Sin pSEO</p>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Cobertura pSEO</span>
                  <span>{pct}%</span>
                </div>
                <Progress value={pct} className="h-2" />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPseoConfig;
